import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import './LandingPage.css';
import logo from '../../assets/Logo - Side.png';

function LandingPage() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/plans`);
      if (res.ok) {
        const data = await res.json();
        setPlans(data.plans || []);
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const featureLabels = {
    basic_dashboard: 'Dashboard',
    tenant_portal: 'Tenant Portal',
    maintenance_requests: 'Maintenance Tracking',
    document_storage: 'Document Storage',
    email_notifications: 'Email Notifications',
    payment_tracking: 'Payment Tracking',
    stripe_payments: 'Online Payments',
    automated_reminders: 'Automated Reminders',
    financial_reports: 'Financial Reports',
    applicant_screening: 'Applicant Screening',
    api_access: 'API Access',
    white_label: 'White Label',
    priority_support: 'Priority Support',
    custom_integrations: 'Custom Integrations'
  };

  const formatLimit = (value) => {
    if (value === -1) return 'Unlimited';
    return value.toString();
  };

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <div className="nav-container">
          <img src={logo} alt="RentafAI" className="nav-logo" />
          <div className="nav-links">
            <a href="#features">Features</a>
            <a href="#pricing">Pricing</a>
            <Link to="/" className="nav-btn nav-btn-outline">Log In</Link>
            <Link to="/signup" className="nav-btn nav-btn-primary">Start Free Trial</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          <h1 className="hero-title">
            Property Management,<br />
            <span className="gradient-text">Simplified.</span>
          </h1>
          <p className="hero-subtitle">
            Streamline rent collection, tenant communication, maintenance tracking, 
            and applicant management — all in one modern platform.
          </p>
          <div className="hero-cta">
            <Link to="/signup" className="btn btn-primary btn-lg">
              Start Your Free Trial
            </Link>
            <Link to="/demo" className="btn btn-outline btn-lg">
              Watch Demo
            </Link>
          </div>
          <p className="hero-note">No credit card required. 14-day free trial.</p>
        </div>
        <div className="hero-visual">
          <div className="dashboard-preview">
            <div className="preview-header">
              <div className="preview-dots">
                <span></span><span></span><span></span>
              </div>
            </div>
            <div className="preview-content">
              <div className="preview-sidebar"></div>
              <div className="preview-main">
                <div className="preview-card"></div>
                <div className="preview-card"></div>
                <div className="preview-card"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section">
        <div className="section-header">
          <h2>Everything You Need to Manage Properties</h2>
          <p>Powerful features designed for landlords and property managers</p>
        </div>
        
        <div className="features-grid">
          <div className="feature-card">
            <div className="feature-icon">💳</div>
            <h3>Online Rent Collection</h3>
            <p>Accept rent payments via ACH or card. Automatic receipts and late fee tracking.</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">📱</div>
            <h3>Tenant Portal</h3>
            <p>Self-service portal for tenants to pay rent, submit requests, and access documents.</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">🔧</div>
            <h3>Maintenance Tracking</h3>
            <p>Track work orders from submission to completion with photo uploads and updates.</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">📝</div>
            <h3>Application Screening</h3>
            <p>Online applications with integrated background and credit checks.</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">📄</div>
            <h3>Document Management</h3>
            <p>Store and e-sign leases, notices, and important documents securely.</p>
          </div>
          
          <div className="feature-card">
            <div className="feature-icon">📊</div>
            <h3>Financial Reports</h3>
            <p>Income statements, expense tracking, and tax-ready reports at your fingertips.</p>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section">
        <div className="section-header">
          <h2>Simple, Transparent Pricing</h2>
          <p>Choose the plan that fits your portfolio size</p>
        </div>

        {loading ? (
          <div className="loading-spinner">Loading plans...</div>
        ) : (
          <div className="pricing-grid">
            {plans.map(plan => (
              <div 
                key={plan.id} 
                className={`pricing-card ${plan.is_featured ? 'featured' : ''}`}
              >
                {plan.is_featured && <div className="featured-badge">Most Popular</div>}
                <div className="plan-header">
                  <h3>{plan.name}</h3>
                  <p className="plan-description">{plan.description}</p>
                </div>
                <div className="plan-price">
                  <span className="price-amount">${plan.price_monthly}</span>
                  <span className="price-period">/month</span>
                </div>
                {plan.price_yearly > 0 && (
                  <p className="yearly-price">
                    or ${plan.price_yearly}/year (save ${(plan.price_monthly * 12 - plan.price_yearly).toFixed(0)})
                  </p>
                )}
                <div className="plan-limits">
                  <div className="limit-item">
                    <span className="limit-value">{formatLimit(plan.max_properties)}</span>
                    <span className="limit-label">Properties</span>
                  </div>
                  <div className="limit-item">
                    <span className="limit-value">{formatLimit(plan.max_units)}</span>
                    <span className="limit-label">Units</span>
                  </div>
                  <div className="limit-item">
                    <span className="limit-value">{formatLimit(plan.max_users)}</span>
                    <span className="limit-label">Users</span>
                  </div>
                </div>
                <ul className="plan-features">
                  {plan.features.slice(0, 6).map((feature, idx) => (
                    <li key={idx}>
                      <span className="check">✓</span>
                      {featureLabels[feature] || feature}
                    </li>
                  ))}
                  {plan.features.length > 6 && (
                    <li className="more-features">+{plan.features.length - 6} more features</li>
                  )}
                </ul>
                <Link 
                  to={`/signup?plan=${plan.slug}`} 
                  className={`btn btn-block ${plan.is_featured ? 'btn-primary' : 'btn-outline'}`}
                >
                  {plan.price_monthly === 0 ? 'Get Started Free' : 'Start Free Trial'}
                </Link>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-content">
          <h2>Ready to simplify your property management?</h2>
          <p>Join thousands of landlords who've made the switch to RentafAI.</p>
          <Link to="/signup" className="btn btn-primary btn-lg">
            Start Your Free Trial
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="footer-content">
          <div className="footer-brand">
            <img src={logo} alt="RentafAI" />
            <p>Smart property management for modern landlords.</p>
          </div>
          <div className="footer-links">
            <div className="footer-column">
              <h4>Product</h4>
              <a href="#features">Features</a>
              <a href="#pricing">Pricing</a>
              <Link to="/demo">Demo</Link>
            </div>
            <div className="footer-column">
              <h4>Company</h4>
              <a href="#about">About</a>
              <a href="#contact">Contact</a>
              <a href="#careers">Careers</a>
            </div>
            <div className="footer-column">
              <h4>Legal</h4>
              <a href="#privacy">Privacy Policy</a>
              <a href="#terms">Terms of Service</a>
            </div>
          </div>
        </div>
        <div className="footer-bottom">
          <p>&copy; {new Date().getFullYear()} RentafAI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
